import React, { useEffect, useState } from "react";
import { User } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { LeagueGwScorers, fetchLeagueGwScorers } from "../../api/leagues";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import { panelHeading } from "../../styles";

interface Props {
  user: User;
  leagueId: string;
  resultsGwId: string | null;
}

const PAGE_SIZE = 10;

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  marginBottom: "1rem",
  overflow: "hidden",
};

export default function GwScorersPanel({ user, leagueId, resultsGwId }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<LeagueGwScorers | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!resultsGwId) { setData(null); return; }
    setPage(0);
    fetchLeagueGwScorers(user, leagueId, resultsGwId).then(setData).catch(() => {});
  }, [leagueId, resultsGwId]); // eslint-disable-line

  if (!data || data.gameweek.scorers.length === 0) return null;

  const scorers = data.gameweek.scorers;
  const totalPages = Math.ceil(scorers.length / PAGE_SIZE);
  const pageScorers = scorers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div style={panelStyle}>
      <div style={{ padding: "0.75rem 1.25rem 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ ...panelHeading, margin: 0 }}>
          {t("fixtures.goalscorers")} · {data.gameweek.name}
        </h2>
        {totalPages > 1 && (
          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, scorers.length)} of {scorers.length}
          </span>
        )}
      </div>
      <div style={{ marginTop: "0.5rem" }}>
        {pageScorers.map((s) => {
          const cfg = getClubBadge(s.playerClub);
          return (
            <div key={s.playerId} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.45rem 1.25rem",
              borderBottom: "1px solid var(--color-border-subtle)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {cfg && <ClubBadge config={cfg} size={16} title={s.playerClub} />}
                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>{s.playerName}</span>
              </div>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--color-accent)", minWidth: 28, textAlign: "right" }}>
                {s.goals} {s.goals === 1 ? t("fixtures.goal") : t("fixtures.goals")}
              </span>
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.4rem 1.25rem" }}>
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
            style={{
              background: "none",
              border: "none",
              cursor: page === 0 ? "default" : "pointer",
              fontSize: "0.7rem",
              fontWeight: 700,
              color: page === 0 ? "var(--color-border)" : "var(--color-accent)",
              padding: "0.2rem 0",
            }}
          >
            {t("fixtures.prev")}
          </button>
          <span style={{ fontSize: "0.65rem", color: "var(--color-text-muted)" }}>
            {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages - 1}
            style={{
              background: "none",
              border: "none",
              cursor: page === totalPages - 1 ? "default" : "pointer",
              fontSize: "0.7rem",
              fontWeight: 700,
              color: page === totalPages - 1 ? "var(--color-border)" : "var(--color-accent)",
              padding: "0.2rem 0",
            }}
          >
            {t("fixtures.next")}
          </button>
        </div>
      )}
    </div>
  );
}
